import React, { useEffect, useState } from 'react';
import styles from '../insertsPages.module.css';
import { Tabs } from '../../../Tabs';
import { useGiftWrapContext } from '../../../../context/GiftWrapContext';
import { Chooser } from '../../../Chooser';
import { useUserInput } from '../../../../context/UserInputContext.tsx';
import { Visualiser, VisualiserTextBox } from '../../../Visualiser/index';
import { getInsertsAndCardsProduct, findVariantForProduct } from '../../../../utilities/visualiserVariants.js';

/**
 * InsertMessage component allows users to add a message for their box insert.
 *
 * @param {Object} props - Component props.
 * @returns {import('react').JSX.Element}
 */
export function InsertMessage({ ...rest }) {
  const { getVisualiserChoice, setVisualiserChoice } = useGiftWrapContext();

  // Use UserInputContext directly as the state
  const { getTextInput, setTextInput, setVisualiserSelection, clearVisualiserSelection } = useUserInput();
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedSuggestionGroup, setSelectedSuggestionGroup] = useState(0);
  const [chooserOpen, setChooserOpen] = useState(false);

  const messageAreaName = 'Insert Card > Text Only > Front';

  let suggestedMessages = (window?.giftWrapData?.insertsAndCards?.greetingsCardTemplateGroups || []).filter((group) =>
    group.area?.includes(messageAreaName)
  );

  // Normalize suggestions to a string array for safe rendering.
  // giftWrapData types are intentionally loose, so we guard at runtime.
  const selectedSuggestions = suggestedMessages?.[selectedSuggestionGroup]?.suggestions;
  const suggestionList = Array.isArray(selectedSuggestions) ? selectedSuggestions : [];

  useEffect(() => {
    const productKey = 'inserts';
    const product = getInsertsAndCardsProduct({ productKey });

    // Match the known "Text" insert variant.
    const variantTitle = 'Text';
    const variant = findVariantForProduct({ product, variantTitle, fallbackOption1: 'Text' });

    setVisualiserSelection({
      productKey,
      variant,
      config: {
        templateId: 'INSERT_CUSTOM_TEXT_ONLY',
        mapping: {
          text: {
            'insertMessage.front': 'UVFrontText',
            'insertMessage.back': 'UVBackText'
          }
        }
      }
    });

    return () => {
      clearVisualiserSelection();
    };
  }, [setVisualiserSelection, clearVisualiserSelection]);

  return (
    <div className={styles['visualiser-stack']}>
      <div className={styles['page--container']}>
        <Tabs
          tabs={[
            { id: 'front', label: 'Front' },
            { id: 'back', label: 'Back' }
          ]}
          activeIndex={activeIndex}
          onChange={setActiveIndex}
          fullWidth={true}
        />

        {activeIndex === 0 && (
          <Visualiser
            type="INSERT"
            background="var(--color-white)"
            subtitle="Tap to edit"
          >
            <VisualiserTextBox
              size={{
                width: '89%',
                minWidth: '250px',
                maxWidth: '280px',
                minHeight: '60px',
                maxHeight: '250px'
              }}
              position={{
                top: '50%',
                left: '50%'
              }}
              font="Amalfi Coast"
              fontSize={28}
              lineHeight={1.7}
              fontWeight='600'
              maxLength={40}
              maxLines={3}
              style={{
                paddingTop: '10px',
                transform: 'translate(-50%, -50%)'
              }}
              value={getTextInput('insertMessage.front')}
              setValue={((value) => setTextInput('insertMessage.front', value))}
            />
          </Visualiser>
        )}

        {activeIndex === 1 && (
          <Visualiser
            type="INSERT"
            background={`url(${window?.giftWrapData?.insertsAndCards?.images?.insertBack})`}
            subtitle="Tap to edit"
          >
            <VisualiserTextBox
              size={{
                width: '280px',
                minWidth: '200px',
                maxWidth: '280px',
                minHeight: '50px',
                maxHeight: '200px'
              }}
              position={{
                top: '50%',
                left: '50%'
              }}
              fontWeight="bold"
              maxLength={100}
              maxLines={8}
              lineHeight={1.2}
              fontSize={21}
              style={{
                transform: 'translate(-50%, -50%)'
              }}
              value={getTextInput('insertMessage.back')}
              setValue={((value) => setTextInput('insertMessage.back', value))}
            />
          </Visualiser>
        )}
      </div>

      {activeIndex === 0 && (
        <Chooser title="Suggested Messages" open={chooserOpen} onToggle={setChooserOpen}>
          <div className={styles['suggested-messages--container']}>
            <div className={styles['suggested-messages--column']}>
              {suggestedMessages.map((group, index) => {
                const isSelected = selectedSuggestionGroup === index;
                return (
                  <div
                    key={`${group.id}`}
                    className={`${styles['suggested-messages--item']} ${isSelected ? styles['suggested-messages--item-selected'] : ''}`}
                    onClick={() => setSelectedSuggestionGroup(index)}
                  >
                    <p>{`${group.name}`}</p>
                  </div>
                );
              })}
            </div>
            <div className={styles['suggested-messages--column']} style={{ marginLeft: '20px' }}>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {suggestionList.map((suggestion) => {
                  return (
                    <li key={suggestion} style={{ padding: '10px 0' }}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setTextInput('insertMessage.front', suggestion);
                          setActiveIndex(1);
                          setTimeout(() => {
                            setActiveIndex(0);
                            setChooserOpen(false);
                          }, 100);
                        }}
                      >
                        {suggestion}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </Chooser>
      )}
    </div>
  );
}
